import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import ExportIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Export.js';
import { Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import React from 'react';
import { Dropdown } from '../../molecules/Dropdown';
import { useExportLog } from '../../hooks';
import { EXPORT_LOG_PRESETS, PresetKey } from '../../utils';

export const ExportLogButton: React.FC = () => {
  const { tokens } = useTheme<LensTheme>();
  const sp = tokens.semantic.spacing.desktop.gap;
  const color = tokens.semantic.color;

  const {
    drawerOpen,
    setDrawerOpen,
    selectedPreset,
    setSelectedPreset,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    loading,
    handleClose,
    handleGenerate,
    isGenerateDisabled,
  } = useExportLog();

  return (
    <>
      <Button
        variant="outlined"
        onClick={() => setDrawerOpen(true)}
        startIcon={<ExportIcon />}
        data-testid="export-log-button"
        sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
      >
        Export log
      </Button>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleClose}
        ModalProps={{ keepMounted: false }}
        data-testid="export-log-drawer"
        sx={{
          '& .MuiDrawer-paper': {
            width: '400px',
            padding: sp[3].value,
            backgroundColor: color.surface.default.value,
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '100vh',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexShrink: 0,
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontSize: '24px',
              fontWeight: tokens.semantic.fontWeight.emphasis.value,
              color: color.type.default.value,
            }}
          >
            Export log
          </Typography>
          <IconButton
            onClick={handleClose}
            aria-label="close"
            data-testid="export-log-drawer-close"
            sx={{
              color: color.action.secondary.onSecondary.value,
              '&:hover': {
                backgroundColor: color.action.secondary.hoverFill.value,
              },
            }}
          >
            <Close />
          </IconButton>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            padding: sp[1].value,
            mt: sp[2].value,
          }}
        >
          <Dropdown
            value={selectedPreset}
            onChange={(value) => setSelectedPreset(value as PresetKey)}
            options={EXPORT_LOG_PRESETS}
            data-testid="export-log-preset-dropdown"
          />

          <Box>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: tokens.semantic.fontWeight.emphasis.value,
                color: color.type.default.value,
                mb: 1,
              }}
            >
              Custom date range
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                type="date"
                size="small"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                fullWidth
                slotProps={{
                  htmlInput: { max: toDate || undefined },
                }}
              />
              <TextField
                type="date"
                size="small"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                fullWidth
                slotProps={{
                  htmlInput: { min: fromDate || undefined },
                }}
              />
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pt: sp[3].value,
            flexShrink: 0,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClose}
            data-testid="export-log-drawer-cancel"
            sx={{
              textTransform: 'none',
              minWidth: '88px',
              height: '40px',
              borderRadius: tokens.semantic.radius.lg.value,
              borderColor: color.type.default.value,
              color: color.type.default.value,
              '&:hover': {
                borderColor: color.type.default.value,
                backgroundColor: 'transparent',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={isGenerateDisabled}
            loading={loading}
            data-testid="export-log-drawer-generate"
            disableElevation
            sx={{
              textTransform: 'none',
              minWidth: '88px',
              height: '40px',
              borderRadius: tokens.semantic.radius.lg.value,
              backgroundColor: color.type.default.value,
              color: color.surface.default.value,
              '&:hover': {
                backgroundColor: color.type.default.value,
                opacity: 0.9,
              },
              '&.Mui-disabled': {
                cursor: 'not-allowed',
                backgroundColor: color.ui.divider.default.value,
                color: color.type.muted.value,
              },
            }}
          >
            Generate log
          </Button>
        </Box>
      </Drawer>
    </>
  );
};
